// Per-dispute Enrichment data: structured, transaction-specific facts that
// strengthen the evidence PDF but are distinct from the two things the page
// already handles elsewhere: prose (the Evidence editor's blocks) and file
// uploads (the Evidence locker, populated in Configure).
//
// The field list mirrors a competitor's enrichment form and is intentionally a
// PLACEHOLDER set: the names, types and grouping are expected to be revised
// once the real contract is agreed. Keys are our own snake_case (not the
// competitor's), so renaming a label later doesn't move stored data.
//
// Two kinds of section:
//   ENRICHMENT_SECTIONS       - single-value fields, grouped by topic.
//   ENRICHMENT_ARRAY_SECTIONS - repeatable rows (shipping, order items, usage
//                               logs, refunds), each row holding the same fields.
// Field `type` drives the control EnrichmentField renders:
// 'text' | 'textarea' | 'number' | 'select' | 'date' | 'boolean'.
export const ENRICHMENT_SECTIONS = [
  {
    key: "billing",
    label: "Billing",
    fields: [
      { key: "billing_first_name", label: "First Name", type: "text" },
      { key: "billing_last_name", label: "Last Name", type: "text" },
      { key: "billing_email_address", label: "Email Address", type: "text" },
      { key: "customer_phone", label: "Customer Phone", type: "text" },
      { key: "billing_address_1", label: "Address 1", type: "text" },
      { key: "billing_address_2", label: "Address 2", type: "text" },
      { key: "billing_city", label: "City", type: "text" },
      { key: "billing_state", label: "State", type: "text" },
      { key: "billing_zip", label: "Zip", type: "text" },
      { key: "billing_country", label: "Country", type: "text", placeholder: "e.g. FR" }
    ]
  },
  {
    key: "card_authorization",
    label: "Card and authorization",
    fields: [
      {
        key: "payment_method_type",
        label: "Payment Method Type",
        type: "select",
        options: [
          { value: "creditCard", label: "Credit card" },
          { value: "debitCard", label: "Debit card" },
          { value: "prepaidCard", label: "Prepaid card" },
          { value: "applePay", label: "Apple Pay" },
          { value: "googlePay", label: "Google Pay" },
          { value: "other", label: "Other" }
        ]
      },
      {
        key: "card_issuing_country",
        label: "Card Issuing Country",
        type: "text",
        placeholder: "e.g. FR"
      },
      { key: "bin", label: "BIN", type: "text" },
      { key: "card_fingerprint", label: "Card Fingerprint", type: "text" },
      { key: "apple_last_4_digits", label: "Apple Last 4 Digits", type: "text" },
      { key: "billing_descriptor", label: "Billing Descriptor", type: "text" },
      { key: "authorization_response_code", label: "Authorization Response Code", type: "text" },
      { key: "authorization_response_text", label: "Authorization Response Text", type: "text" },
      { key: "authorization_created_at", label: "Authorization Created at", type: "date" }
    ]
  },
  {
    key: "verification_results",
    label: "Verification results",
    fields: [
      { key: "avs_results", label: "AVS results", type: "text" },
      { key: "avs_results_postal_code", label: "AVS results - Postal Code", type: "text" },
      { key: "avs_results_address_line", label: "AVS results - Address Line", type: "text" },
      {
        key: "cvv_results",
        label: "CVV Results",
        type: "select",
        options: [
          { value: "pass", label: "Pass" },
          { value: "fail", label: "Fail" },
          { value: "unavailable", label: "Unavailable" },
          { value: "not_provided", label: "Not provided" }
        ]
      },
      { key: "cvv_timestamp", label: "CVV date/timestamp", type: "date" },
      { key: "three_ds_eci_code", label: "3D Secure - ECI Code", type: "text" },
      { key: "three_ds_eci_description", label: "3D Secure - ECI Description", type: "text" },
      { key: "three_ds_cavv_response", label: "3D Secure - CAVV Response", type: "text" }
    ]
  },
  {
    key: "transaction_context",
    label: "Transaction context",
    fields: [
      {
        key: "transaction_status",
        label: "Transaction Status",
        type: "select",
        options: [
          { value: "succeeded", label: "Succeeded" },
          { value: "pending", label: "Pending" },
          { value: "failed", label: "Failed" },
          { value: "refunded", label: "Refunded" }
        ]
      },
      { key: "transaction_timestamp", label: "Transaction Timestamp", type: "date" },
      {
        key: "count_of_previous_orders",
        label: "Count of Previous Orders",
        type: "number",
        min: 0
      },
      { key: "device_id", label: "Device ID", type: "text" },
      { key: "user_agent", label: "User Agent", type: "text" }
    ]
  },
  {
    key: "account_activity",
    label: "Account activity",
    fields: [
      { key: "account_creation_timestamp", label: "Account Creation Timestamp", type: "date" },
      {
        key: "account_last_login_timestamp",
        label: "Account Last Successful Login - Timestamp",
        type: "date"
      },
      {
        key: "digital_item_download_timestamp",
        label: "Digital Item Download - Timestamp",
        type: "date"
      }
    ]
  },
  {
    key: "store_credit",
    label: "Store credit",
    fields: [
      { key: "store_credit_amount", label: "Store Credit - Amount", type: "number", min: 0 },
      { key: "store_credit_currency", label: "Store Credit - Currency", type: "text" },
      { key: "store_credit_timestamp", label: "Store Credit - Timestamp", type: "date" },
      { key: "store_credit_reason", label: "Store Credit - Reason", type: "text" },
      {
        key: "store_credit_redeemed_amount",
        label: "Store Credit Redeemed - Amount",
        type: "number",
        min: 0
      },
      {
        key: "store_credit_redeemed_timestamp",
        label: "Store Credit Redeemed - Timestamp",
        type: "date"
      }
    ]
  }
]

// Repeatable sections: the merchant can add as many rows as the case needs (one
// shipment, several order lines, a list of refunds). Values live under the
// section key as an array of row objects, e.g.
// enrichment_fields.transaction_order_items = [{ product_name: 'Plan', ... }]
export const ENRICHMENT_ARRAY_SECTIONS = [
  {
    key: "transaction_shipping",
    label: "Transaction shipping",
    addLabel: "Add Transaction shipping",
    fields: [{ key: "delivery_pickup_date", label: "Delivery/Pickup Date", type: "date" }]
  },
  {
    key: "transaction_order_items",
    label: "Transaction order items",
    addLabel: "Add Transaction order items",
    fields: [
      { key: "product_name", label: "Products/Services - Name", type: "text" },
      {
        key: "product_price_value",
        label: "Products/Services - Price Value",
        type: "number",
        min: 0
      }
    ]
  },
  {
    key: "transaction_usage_items",
    label: "Transaction usage items",
    addLabel: "Add Transaction usage items",
    fields: [
      { key: "usage_user_action", label: "Usage Logs - User Action", type: "text" },
      { key: "usage_product_name", label: "Usage Logs - Product/Service Name", type: "text" },
      { key: "usage_timestamp", label: "Usage Logs - Timestamp", type: "date" },
      { key: "usage_id", label: "Usage Logs - ID", type: "text" }
    ]
  },
  {
    key: "transaction_refund_items",
    label: "Transaction refund items",
    addLabel: "Add Transaction refund items",
    fields: [
      { key: "refund_status", label: "Refunds - Status", type: "text" },
      { key: "refund_amount", label: "Refunds - Amount", type: "number", min: 0 },
      { key: "refund_currency", label: "Refunds - Currency", type: "text" },
      { key: "refund_timestamp", label: "Refunds - Timestamp", type: "date" },
      { key: "refund_reason", label: "Refunds - Reason", type: "text" }
    ]
  }
]

// The x/y stat counts the single-value fields only, so the denominator is a
// fixed, meaningful target. Repeatable rows are open-ended (adding a row would
// otherwise lower the score), so they show their own row count instead.
export const ENRICHMENT_FIELD_COUNT = ENRICHMENT_SECTIONS.reduce((n, s) => n + s.fields.length, 0)

// A field counts as filled once it has a real value; a boolean only counts once
// explicitly set true (matches "confirm this" semantics: unchecked reads as
// "not yet confirmed", not as a deliberate "no").
const isFieldFilled = (field, value) => {
  if (field.type === "boolean") return value === true
  if (field.type === "number") return value !== "" && value != null
  return value != null && String(value).trim() !== ""
}

export const enrichmentFillCount = (values) => {
  const v = values || {}
  let n = 0
  ENRICHMENT_SECTIONS.forEach((section) => {
    section.fields.forEach((field) => {
      if (isFieldFilled(field, v[field.key])) n += 1
    })
  })
  return n
}

export const enrichmentFillPct = (values) => {
  if (!ENRICHMENT_FIELD_COUNT) return 0
  return Math.round((enrichmentFillCount(values) / ENRICHMENT_FIELD_COUNT) * 100)
}

// A blank row for a repeatable section, so "Add" always yields a fully-shaped
// object rather than growing keys one edit at a time.
export const emptyEnrichmentRow = (section) =>
  section.fields.reduce((row, field) => ({ ...row, [field.key]: "" }), {})

// The 3 bands the enrichment gauges colour by. A band name rather than a hex, so CSS
// can theme the pill (a raw text colour can't invert for dark mode).
export const enrichmentBand = (pct) => (pct >= 67 ? "high" : pct >= 34 ? "mid" : "low")
